import { Button, Kind } from '@thanx/uikit/button'
import { Modal } from '@thanx/uikit/modal'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { buildTranslate } from 'locales'
import React from 'react'

const t = buildTranslate('live_updates.unsaved_modal')
const tPublish = buildTranslate('live_updates.publish_modal')

const MODAL_Z_INDEX = 1200
const CONTENT_MAX_WIDTH = '600px'
const DIALOG_MARGIN = '3rem'
const DIALOG_BORDER_RADIUS = '8px'

export interface UnsavedChangesModalProps {
  isOpen: boolean
  // Publish mutation in flight — disables both write actions (double-click guard).
  isPublishing: boolean
  publishTurnsOn?: boolean
  onDiscard: () => void
  onKeepEditing: () => void
  onPublish: () => void
}

// FR-43 unsaved-changes dialog (Figma 433-1938): a 3-action fork shown when the
// merchant navigates away with pending edits. "Discard changes" throws the edits
// away and leaves; "Keep editing" stays; "Publish changes" publishes, then
// leaves on success.
const UnsavedChangesModal = ({
  isOpen,
  isPublishing,
  publishTurnsOn = false,
  onDiscard,
  onKeepEditing,
  onPublish,
}: UnsavedChangesModalProps) => {
  const [css] = useStyletron()

  return (
    <Modal
      isOpen={isOpen}
      // Escape/backdrop dismiss must freeze the same as the buttons below —
      // otherwise it's the one escape hatch a merchant can still trigger
      // while a publish is in flight.
      onClose={isPublishing ? undefined : onKeepEditing}
      size="auto"
      closeType="small"
      zIndex={MODAL_Z_INDEX}
      overrides={{
        Dialog: {
          style: () => ({
            marginTop: DIALOG_MARGIN,
            marginRight: DIALOG_MARGIN,
            marginBottom: DIALOG_MARGIN,
            marginLeft: DIALOG_MARGIN,
            // !important beats baseui's same-specificity Dialog borderRadius rule (Styletron injection-order race).
            borderRadius: `${DIALOG_BORDER_RADIUS} !important`,
          }),
        },
      }}
    >
      <div
        className={`p-xl d-flex flex-column ${css({
          maxWidth: CONTENT_MAX_WIDTH,
        })}`}
      >
        <Text.Header3 tag="h3" className="m-0">
          {t('title')}
        </Text.Header3>
        <Text.BodyText3 color="grey70" className="mt-l mb-xl">
          {t('body')}
        </Text.BodyText3>
        {publishTurnsOn && (
          <Text.BodyText3 color="grey70" className="mb-xl">
            {tPublish('body_turn_on')}
          </Text.BodyText3>
        )}
        <div
          className={`d-flex justify-content-center ${css({
            whiteSpace: 'nowrap',
          })}`}
        >
          <Button
            kind={Kind.MINIMAL}
            // No visible border, so its own right padding reads as extra gap on top of mr-m — drop it to match the other buttons' spacing.
            className={`mr-m ${css({ flexShrink: 0, paddingRight: 0 })}`}
            disabled={isPublishing}
            onClick={onDiscard}
          >
            {t('discard')}
          </Button>
          <Button
            kind={Kind.SECONDARY}
            className={`mr-m ${css({ flexShrink: 0 })}`}
            disabled={isPublishing}
            onClick={onKeepEditing}
          >
            {t('keep_editing')}
          </Button>
          <Button
            className={css({ flexShrink: 0 })}
            disabled={isPublishing}
            onClick={onPublish}
          >
            {t('publish')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default UnsavedChangesModal
